import { isEmpty } from "lodash";
import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { RaceProgram } from "@tvg/ts-types/Race";
import { useWagerProfile, usePreviousNonNullish } from "@tvg/custom-hooks";
import { useSelector } from "react-redux";
import { getBetType, getRaceOfBet } from "@tvg/desktop-bet/src/store/selectors";
import queryHelper from "../graphql/options.graph";
import { GET_RACES_BETTING_INTERESTS } from "../graphql/queries/RaceBettingInterestQuery";

const withRacesBiComplete = (betType: number, currentRace?: RaceProgram) => {
  const wagerType = currentRace?.wagerTypes?.find(
    (wager) => wager.type.id === betType
  );

  return !!wagerType && !!betType && wagerType.legCount > 1;
};

const useRacesBettingInterest = (
  currentRace?: RaceProgram,
  races?: RaceProgram[],
  isAccountCompliant?: boolean
) => {
  // prevent request if all races were requested
  const [hasRequestedFull, setHasRequestedFull] = useState(false);
  const [raceIdsBiComplete, setRaceIdsBiComplete] = useState<number[]>([]);
  const [raceIdsBiPartial, setRaceIdsBiPartial] = useState<number[]>([]);
  const wagerProfile: string = useWagerProfile();
  const raceOfBet = useSelector(getRaceOfBet);
  const betType = useSelector(getBetType);
  useEffect(
    () => () => {
      // clear all races request on track change
      setHasRequestedFull(false);
    },
    [currentRace?.id]
  );

  useEffect(() => {
    // skip until betType is defined to request the correct number of races
    const skip = currentRace?.id ? raceOfBet !== currentRace.id : true;
    // decide if we are requesting 1 race or all
    const loadMore = skip ? false : withRacesBiComplete(betType, currentRace);

    if (loadMore || hasRequestedFull) {
      setRaceIdsBiComplete(
        (races || []).map((race) => race.tvgRaceId as number)
      );
      setRaceIdsBiPartial([]);
      setHasRequestedFull(true);
    } else if (currentRace?.tvgRaceId) {
      setRaceIdsBiComplete([currentRace.tvgRaceId]);
      setRaceIdsBiPartial(
        ((!skip && races) || []).map((race) => race.tvgRaceId as number)
      );
    }
  }, [betType, raceOfBet, races?.length]);

  // request races data if it does not exist on the cache
  const { data, previousData, loading, fetchMore } = useQuery(
    GET_RACES_BETTING_INTERESTS,
    queryHelper.apolloOptionsBi({
      tvgRaceIds: raceIdsBiComplete,
      tvgRaceIdsBiPartial: raceIdsBiPartial,
      wagerProfile,
      isAccountCompliant: !!isAccountCompliant
    })
  );
  // return previous data while we wait for new one
  const racesBI: Array<RaceProgram> = data?.races ?? previousData?.races;
  // select bi from current race
  const currentRaceBI =
    racesBI && racesBI.find((race: RaceProgram) => race.id === currentRace?.id);

  // save previous currentRace until we have the new one
  const previousCurrentRaceData = usePreviousNonNullish(currentRaceBI);

  // return only the new data
  let racesBIPartial: Array<RaceProgram> =
    data?.racesBiPartial ?? previousData?.racesBiPartial;

  if (!hasRequestedFull && racesBIPartial?.length && currentRaceBI) {
    const currentRaceIdx = racesBIPartial.findIndex(
      (raceBi) => raceBi.id === currentRace?.id
    );
    if (currentRaceIdx >= 0) {
      racesBIPartial = [
        ...racesBIPartial.slice(0, currentRaceIdx),
        currentRaceBI,
        ...racesBIPartial.slice(currentRaceIdx + 1)
      ];
    }
  }

  return {
    racesBI: hasRequestedFull ? racesBI : racesBIPartial || [],
    currentRaceBI: currentRaceBI || previousCurrentRaceData,
    isStaleData: isEmpty(currentRaceBI),
    loading,
    fetchMore: () =>
      fetchMore({
        variables: {
          tvgRaceIds: raceIdsBiComplete,
          tvgRaceIdsBiPartial: raceIdsBiPartial,
          wagerProfile
        }
      })
  };
};

export default useRacesBettingInterest;
